import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, Download } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";
import { YMark } from "@/components/site/Logo";
import { SignaturePad } from "@/components/contract/SignaturePad";
import { ParentSign } from "@/components/contract/ParentSign";
import { DetailsFields, emptyDetails } from "@/components/contract/DetailsFields";

export default function ContractSign() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [missing, setMissing] = useState(false);
  const [details, setDetails] = useState(emptyDetails);
  const [sig, setSig] = useState("");
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [parent, setParent] = useState({ details: emptyDetails(), sig: "", accept: false });

  useEffect(() => {
    api.get(`/contracts/${token}`).then((r) => {
      setInfo(r.data);
      const pre = r.data?.prefill || {};
      if (pre.details) setDetails({ ...emptyDetails(), ...pre.details });
      if (pre.parent_details) setParent((p) => ({ ...p, details: { ...emptyDetails(), ...pre.parent_details } }));
    }).catch(() => setMissing(true));
  }, [token]);

  const sign = async (e) => {
    e.preventDefault();
    if (!sig) return toast.error("Please draw your signature with the mouse or your finger.");
    if (info.is_minor && !parent.sig) return toast.error("The parent or guardian must draw their signature too.");
    setBusy(true);
    try {
      const { data } = await api.post(`/contracts/${token}/sign`, {
        details, signature: sig, accept,
        ...(info.is_minor ? { parent_details: parent.details, parent_signature: parent.sig, parent_accept: parent.accept } : {}),
      });
      setInfo((i) => ({ ...i, status: "signed", signed_at: data.signed_at }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (missing) return <main className="flex min-h-screen items-center justify-center bg-white px-5 text-center font-display text-4xl uppercase">Contract not found.</main>;
  if (!info) return <main className="min-h-screen bg-white" />;
  const pdf = `${API}/contracts/${token}/pdf`;

  return (
    <main data-testid="contract-sign-page" className="min-h-screen bg-white px-5 pb-24 pt-28 text-[#1c1418] md:px-10">
      <div className="mx-auto max-w-4xl">
        {info.account && <Link to="/approved-signcontract" data-testid="contract-back-account" className="mb-6 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] underline"><ArrowLeft className="h-4 w-4" /> La tua area</Link>}
        <YMark className="h-12 w-12" />
        <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-[#1c1418]/60">Contract · Contratto{info.label ? ` · ${info.label}` : ""}</p>
        <h1 className="font-display text-5xl uppercase leading-[0.9] md:text-7xl">{info.model_name}</h1>
        <p className="mt-3 text-sm">{info.account ? "Youth Management" : `Sent by ${info.agent_name}, Youth Management Milano.`}</p>

        <div className="mt-8 hidden border border-[#1c1418]/20 md:block">
          <iframe title="Contract" src={pdf} data-testid="contract-pdf-frame" className="h-[60vh] w-full" />
        </div>
        <a href={pdf} target="_blank" rel="noopener noreferrer" data-testid="contract-open-pdf" className="mt-6 flex w-full items-center justify-center gap-2 border border-[#1c1418] py-4 font-mono text-xs uppercase tracking-[0.2em] md:mt-3 md:inline-flex md:w-auto md:border-0 md:py-0 md:underline">
          <Download className="h-4 w-4 text-[#e9458f]" /> Read the contract (PDF)
        </a>

        {info.status === "signed" ? (
          <div data-testid="contract-signed-state" className="mt-10 flex items-start gap-4 bg-[#f4f4f4] p-6">
            <Check className="h-8 w-8 shrink-0 text-[#e9458f]" />
            <div>
              <p className="font-display text-3xl uppercase">Signed · Firmato</p>
              <p className="mt-1 text-sm">Signed on {new Date(info.signed_at).toLocaleString()}. The PDF above now includes your signature page.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={sign} data-testid="contract-sign-form" className="mt-10 space-y-6 border border-[#1c1418]/20 p-6 md:p-8">
            <p className="font-display text-3xl uppercase">Sign online · Firma online</p>
            <p className="text-sm text-[#1c1418]/70">Fill in your data as in your passport or ID, then sign with the mouse or your finger. Inserisci i tuoi dati come sul passaporto o documento, poi firma con il mouse o con il dito.</p>
            <DetailsFields prefix="contract-model" value={details} onChange={setDetails} />
            <SignaturePad onChange={setSig} hint={info.is_minor ? "Model signs here with mouse or finger" : "Sign here with the mouse or your finger"} />
            <label className="flex items-start gap-3 text-sm">
              <input type="checkbox" required checked={accept} onChange={(e) => setAccept(e.target.checked)} data-testid="contract-accept-checkbox" className="mt-1 h-4 w-4 accent-[#1c1418]" />
              I have read the contract and I accept it. Ho letto il contratto e lo accetto.
            </label>
            {info.is_minor && <ParentSign parent={parent} setParent={setParent} />}
            <button disabled={busy} data-testid="contract-sign-submit" className="w-full bg-[#1c1418] px-3 py-4 font-mono text-[11px] uppercase tracking-[0.15em] text-white disabled:opacity-60 md:text-xs md:tracking-[0.25em]">
              {busy ? "Signing…" : info.is_minor ? "Sign together · Firma entrambi" : "Sign the contract · Firma"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
